import PlusIcon from '@components/icons/PlusIcon';
import { LinearProgress } from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RouteWidgetBaseParameters } from '../../../types';
import useDelivery from '../../../useDelivery';
import CargoTypeInformation from '../CargoTypeInformation';
import TruckTypeInformation from '../TruckTypeInformation';
import { TransportTypeItem } from './TransportTypeItem';
import { FclFtlTransport, FclFtlTransportType } from './types';

interface TransportTypesParametersProps {
  transportTypes: FclFtlTransport[];
  mainLanguage: string;
  BaseData: RouteWidgetBaseParameters;
  isSelectionLoading: boolean;
  isSelectingError: boolean;
}

function TransportTypesParameters({
  transportTypes,
  mainLanguage,
  BaseData,
  isSelectingError,
  isSelectionLoading,
}: TransportTypesParametersProps) {
  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const setRouteParams = useRouteParams().setState;
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();

  const { t } = useTranslation('Common');

  /**
   * Добавление типа траспорта для fcl ftl перевозки
   * @param type
   */
  const addTransportTypeHandler = (type: FclFtlTransportType) => {
    const { id: defaultContainerId } = BaseData.containers[0];
    const { id: defaultTruckId } = BaseData.trucks[0];

    const newTransportType: FclFtlTransport = {
      type,
      weight: 1500,
      transportTypeId:
        type === 'container' ? parseInt(defaultContainerId) : parseInt(defaultTruckId),
    };

    setRouteParams((prevState) => ({
      ...prevState,
      customCargoParameters: {
        ...prevState.customCargoParameters,
        transportTypes: [...prevState.customCargoParameters.transportTypes, newTransportType],
      },
    }));
  };

  const containerTransportType = transportTypes.filter((t) => t.type === 'container');
  const truckTransportType = transportTypes.filter((t) => t.type === 'truck');

  return (
    <div className="transport-types-wrapper">
      {isSelectionLoading && !isSelectingError && (
        <div className="transport-types-loading">
          <LinearProgress />
        </div>
      )}
      <div className="transport-type-column">
        <span className="transport-types-title">{t('Common:Containers')}:</span>
        {containerTransportType.map((container, index) => (
          <TransportTypeItem
            key={`fcl-ftl-transport-${index}`}
            transportTypeIndex={index}
            isDeletable={true}
            transportType={container}
            transportList={BaseData.containers}
            mainLanguage={mainLanguage}
            entityTypeHint={<CargoTypeInformation containers={BaseData.containers} />}
            cargoTitle={t('Common:Container')}
            transportWeightTitle={t('Common:ContainerWeight')}
            transportTypeTitle={t('Common:Volume')}
          />
        ))}
        <IconButton
          title={`+ ${t('Actions:Add')}`}
          className={'add-new-transport-type'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => addTransportTypeHandler('container')}
        >
          <PlusIcon />
        </IconButton>
      </div>
      <div
        className={clsx('transport-type-separator', { 'empty-list': transportTypes.length === 0 })}
      >
        <svg
          className={'replace'}
          width="32"
          height="32"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill={'#9199AA'}
            d="M4.4 7.6l-.8.8L.1 4.9a.5.5 0 010-.8L3.6.6l.8.8L1.7 4H16v1H1.7l2.7 2.6zM0 11h14.3l-2.7-2.6.8-.8 3.5 3.5c.1.2.1.6 0 .8l-3.5 3.5-.8-.8 2.7-2.6H0v-1z"
          />
        </svg>
      </div>
      <div className="transport-type-column">
        <span className="transport-types-title">{t('Common:Trucks')}:</span>
        {truckTransportType.map((truck, index) => (
          <TransportTypeItem
            key={`fcl-ftl-transport-${index}`}
            transportTypeIndex={index}
            isDeletable={true}
            transportType={truck}
            transportList={BaseData.trucks}
            mainLanguage={mainLanguage}
            entityTypeHint={
              <TruckTypeInformation
                trucks={BaseData.trucks}
                units={BaseData.units}
                lang={mainLanguage}
              />
            }
            cargoTitle={t('Common:Auto')}
            transportWeightTitle={t('Common:TruckWeight')}
            transportTypeTitle={t('Common:VolumeTuck')}
          />
        ))}
        <IconButton
          title={`+ ${t('Actions:Add')}`}
          className={'add-new-transport-type'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => addTransportTypeHandler('truck')}
        >
          <PlusIcon />
        </IconButton>
      </div>
    </div>
  );
}

export { TransportTypesParameters };
